// src/components/sections/ServicesSection.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, scaleIn } from '../../styles/animations';
import SectionTitle from '../ui/SectionTitle';

const ServicesWrapper = styled.section`
  background-color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: #fff;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};
  padding: ${props => props.theme.spacing.lg};
  transition: all ${props => props.theme.transitions.medium};
  position: relative;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const ServiceIcon = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 3rem;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ServiceTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.primary};
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.textMain};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ServiceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  transition: color ${props => props.theme.transitions.fast};
  
  svg {
    margin-left: 0.5rem;
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const Introduction = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  p {
    font-size: ${props => props.theme.fontSizes.large};
    color: ${props => props.theme.colors.textMain};
  }
`;

const services = [
    {
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h1v7c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-7h1a1 1 0 0 0 .707-1.707l-9-9a.999.999 0 0 0-1.414 0l-9 9A1 1 0 0 0 3 13zm7 7v-5h4v5h-4zm2-15.586 6 6V15l.001 5H16v-5c0-1.103-.897-2-2-2h-4c-1.103 0-2 .897-2 2v5H6v-9.586l6-6z" />
        </svg>,
        title: "Terrasses en bois",
        description: "Créations sur mesure adaptées à tous les espaces extérieurs, avec une sélection de bois de qualité supérieure pour une durabilité optimale.",
        link: "/techniques"
    },
    {
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21.145 19.222L13 15.056V14c1.822-.997 3-2.927 3-5.022 0-3.309-2.691-6-6-6s-6 2.691-6 6c0 2.096 1.178 4.026 3 5.022v1.056l-8.145 4.166a.994.994 0 0 0-.55.898v.792C-1.695 21.916 1.354 24 4.333 24h15.334c2.979 0 6.028-2.084 6.028-3.086v-.792a.994.994 0 0 0-.55-.9zM6 8.978c0-2.212 1.789-4 4-4s4 1.788 4 4-1.789 4-4 4-4-1.788-4-4z" />
        </svg>,
        title: "Terrasses pour piscines",
        description: "Aménagements spécifiques pour les contours de piscine, avec des matériaux résistants à l'eau et antidérapants pour votre sécurité.",
        link: "/techniques"
    },
    {
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
            <path d="M2.414 21.414 3.586 20.5 2 18.414 3.414 17l1.586 1.586L6.414 17 8 18.586 6.586 20l1.586 1.414L6.5 23l-2.086-1.586L2.414 23zM21.414 8.414 22.586 7.5 21 5.414 22.414 4l1.586 1.586L25.414 4 27 5.586 25.586 7l1.586 1.414L25.5 10l-2.086-1.586L21.414 10zM11.414 5.414 12.586 4.5 11 2.414 12.414 1l1.586 1.586L15.414 1 17 2.586 15.586 4l1.586 1.414L15.5 7l-2.086-1.586L11.414 7z" />
        </svg>,
        title: "Parquets de jardin",
        description: "Solutions élégantes pour créer des chemins et des espaces de vie dans votre jardin, alliant esthétique et fonctionnalité.",
        link: "/techniques"
    }
];

const ServicesSection = () => {
    return (
        <ServicesWrapper id="services">
            <ServicesContainer>
                <Introduction>
                    <SectionTitle text="Nos Services" centered />
                    <p>
                        Construite sur mesure avec des matériaux sélectionnés, des techniques éprouvées et le savoir-faire de spécialistes,
                        votre terrasse est bien plus qu'une simple surface en bois.
                    </p>
                </Introduction>

                <ServicesGrid>
                    {services.map((service, index) => (
                        <ServiceCard key={index} delay={`${0.2 * index}s`}>
                            <ServiceTitle>{service.title}</ServiceTitle>
                            <ServiceDescription>{service.description}</ServiceDescription>
                            <ServiceLink to={service.link}>
                                En savoir plus
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M8.5 1.5a.5.5 0 0 0-1 0v11.793l-3.146-3.147a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0-.708-.708L8.5 13.293V1.5z" />
                                </svg>
                            </ServiceLink>
                        </ServiceCard>
                    ))}
                </ServicesGrid>
            </ServicesContainer>
        </ServicesWrapper>
    );
};

export default ServicesSection;